import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Input, Select, Popconfirm, Button, App } from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import api from '../api/client';
import dayjs from 'dayjs';

const { Option } = Select;

export default function AdminUsers() {
  const { message } = App.useApp();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [roleFilter, setRole] = useState('all');

  useEffect(() => {
    api.get('/api/admin/users')
      .then(r => setRows(r.data))
      .catch(() => message.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/api/users/${userId}`);
      setRows(prev => prev.filter(u => u.user_id !== userId));
      message.success('User removed');
    } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to remove user');
    }
  };

  const filtered = rows.filter(u => {
    const matchRole = roleFilter === 'all' || u.role_name === roleFilter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q);
    return matchRole && matchSearch;
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: v => v || <span style={{ color: '#bbb' }}>—</span>,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      render: v => (
        <Tag color={v === 'tutor' ? 'blue' : v === 'admin' ? 'red' : 'green'}>
          {v}
        </Tag>
      ),
      filters: [
        { text: 'Student', value: 'student' },
        { text: 'Tutor',   value: 'tutor' },
        { text: 'Admin',   value: 'admin' },
      ],
      onFilter: (value, record) => record.role_name === value,
    },
    {
      title: 'Level',
      dataIndex: 'current_level',
      render: v => v || <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Age',
      dataIndex: 'age',
      render: v => v ?? <span style={{ color: '#bbb' }}>—</span>,
      sorter: (a, b) => (a.age ?? 0) - (b.age ?? 0),
    },
    {
      title: 'Joined',
      dataIndex: 'created_at',
      render: v => dayjs(v).format('D MMM YYYY'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_, record) => (
        <Popconfirm
          title="Remove this user?"
          description="This will soft-delete the account. It cannot be undone easily."
          onConfirm={() => deleteUser(record.user_id)}
          okText="Remove"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Button type="text" icon={<DeleteOutlined />} size="small" danger />
        </Popconfirm>
      ),
    },
  ];

  const tutors   = rows.filter(u => u.role_name === 'tutor').length;
  const students = rows.filter(u => u.role_name === 'student').length;

  return (
    <AppLayout>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Users</h2>
          <p style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
            {rows.length} total &nbsp;·&nbsp; {tutors} tutors &nbsp;·&nbsp; {students} students
          </p>
        </div>

        <Card bordered={false} style={{ borderRadius: 12 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bbb' }} />}
              placeholder="Search by name or email"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
              allowClear
            />
            <Select value={roleFilter} onChange={setRole} style={{ width: 140 }}>
              <Option value="all">All roles</Option>
              <Option value="student">Students</Option>
              <Option value="tutor">Tutors</Option>
              <Option value="admin">Admins</Option>
            </Select>
          </div>

          <Table
            rowKey="user_id"
            dataSource={filtered}
            columns={columns}
            loading={loading}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            size="small"
          />
        </Card>
      </div>
    </AppLayout>
  );
}
